import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { apiFetch } from "@/hooks/apiFetch"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useAppSelector } from "@/lib/hooks"
import { isAdminPortal } from "@/utilities/domain"
import {
  ReportBranchFilter,
  ReportBranchProvider,
  useReportBranch,
} from "@/components/Reports/ReportBranchFilter"

const localDate = (offset = 0) => {
  const value = new Date()
  value.setDate(value.getDate() + offset)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
}

const errorText = (error) =>
  Object.values(error?.error ?? {})[0]?.[0] ??
  error?.message ??
  "Unable to save offer ranges"

const salesNumber = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
})
const pointsNumber = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 4,
})
const formatSales = (value) => `Rs. ${salesNumber.format(Number(value ?? 0))}`
const formatPoints = (value) => pointsNumber.format(Number(value ?? 0))

export default function IncentivesPage() {
  return (
    <ReportBranchProvider>
      <Incentives />
    </ReportBranchProvider>
  )
}

function Incentives() {
  const permissions = new Set(
    useAppSelector((state) => state.auth.user?.permissions ?? [])
  )
  // Offers and points belong to one branch. The company admin portal reads
  // the selected branch's; they are managed on the branch portals.
  const adminPortal = isAdminPortal()
  const canChange = (permission) => !adminPortal && permissions.has(permission)
  const { branchId } = useReportBranch()
  const branchReady = !adminPortal || Boolean(branchId)
  const branchQuery = branchId ? `branch_id=${branchId}` : ""
  const queryClient = useQueryClient()
  const [date, setDate] = useState(() => localDate())
  const [month, setMonth] = useState(() => localDate().slice(0, 7))
  const [form, setForm] = useState(null)

  const { data: plansResponse, isLoading: plansLoading } = useCustomQuery(
    ["hr-incentive-plans", branchId],
    `staff-incentive-plans?${branchQuery}`,
    { enabled: branchReady }
  )
  const plans = plansResponse?.data?.plans ?? []
  const activePlanId = plansResponse?.data?.active_plan_id

  const { data: dailyResponse, isLoading: dailyLoading } = useCustomQuery(
    ["hr-rewards-daily", date, branchId],
    `staff-rewards/daily?date=${date}&${branchQuery}`,
    { enabled: branchReady && Boolean(date) }
  )
  const daily = dailyResponse?.data
  const { data: monthlyResponse, isLoading: monthlyLoading } = useCustomQuery(
    ["hr-rewards-monthly", month, branchId],
    `staff-rewards/monthly?month=${month}&${branchQuery}`,
    { enabled: branchReady && Boolean(month) }
  )
  const monthly = monthlyResponse?.data

  const save = useMutation({
    mutationFn: () =>
      apiFetch(
        form.id ? `staff-incentive-plans/${form.id}` : "staff-incentive-plans",
        {
          method: form.id ? "PUT" : "POST",
          body: JSON.stringify({
            ...(form.id ? {} : { effective_from: form.effective_from }),
            tiers: form.tiers.map((tier) => ({
              min_sales: tier.min_sales,
              max_sales: tier.max_sales === "" ? null : tier.max_sales,
              points: tier.points,
            })),
          }),
        }
      ),
    onSuccess: () => {
      toast.success("Offer ranges saved")
      setForm(null)
      queryClient.invalidateQueries({ queryKey: ["hr-incentive-plans"] })
      queryClient.invalidateQueries({ queryKey: ["hr-rewards-daily"] })
      queryClient.invalidateQueries({ queryKey: ["hr-rewards-monthly"] })
    },
    onError: (error) => toast.error(errorText(error)),
  })

  const startPlan = () =>
    setForm({
      id: null,
      effective_from: plans.length ? localDate(1) : localDate(),
      tiers: [{ min_sales: "", max_sales: "", points: "" }],
    })
  const editPlan = (plan) =>
    setForm({
      id: plan.id,
      effective_from: plan.effective_from,
      tiers: plan.tiers.map((tier) => ({
        min_sales: tier.min_sales,
        max_sales: tier.max_sales ?? "",
        points: tier.points,
      })),
    })
  const changeTier = (index, field, value) =>
    setForm((current) => ({
      ...current,
      tiers: current.tiers.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, [field]: value } : tier
      ),
    }))

  if (!branchReady) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Typography variant="h5" mb={2}>
          Staff incentives
        </Typography>
        <ReportBranchFilter />
        <Alert severity="info">
          Select a branch to see its offer plans and staff points.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5">Staff incentives</Typography>
          <Typography color="text.secondary">
            Branch offer points are shared among staff marked present on each
            qualifying day.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
          <Button component={Link} to="/pos/attendance" variant="outlined">
            Attendance
          </Button>
          {canChange("hr-incentives-create") && (
            <Button variant="contained" onClick={startPlan}>
              New offer plan
            </Button>
          )}
        </Stack>
      </Stack>

      <ReportBranchFilter />

      <Typography variant="h6" gutterBottom>
        Offer ranges
      </Typography>
      <Typography color="text.secondary" variant="body2" mb={2}>
        Each item on a completed POS order earns the points of the range its
        price per piece (after the item discount) falls in, for every piece
        sold. Items outside every range earn nothing. The day&apos;s points are
        shared among present staff. The starting amount is included and the
        upper limit is excluded.
      </Typography>
      <Stack gap={2} mb={4}>
        {plansLoading ? (
          <Typography sx={{ p: 2 }}>Loading offers...</Typography>
        ) : plans.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            No offer plan has been set for this branch.
          </Paper>
        ) : (
          plans.map((plan) => (
            <Paper key={plan.id} variant="outlined" sx={{ overflow: "hidden" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{ px: 2, py: 1.5, flexWrap: "wrap" }}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Typography variant="body2" fontWeight={600}>
                    From {plan.effective_from}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={plan.id === activePlanId ? "primary" : "default"}
                    label={
                      plan.id === activePlanId
                        ? "Current"
                        : plan.editable
                          ? "Upcoming"
                          : "Past"
                    }
                  />
                </Stack>
                {plan.editable && canChange("hr-incentives-update") && (
                  <Button
                    size="small"
                    aria-label={`Edit offer plan from ${plan.effective_from}`}
                    onClick={() => editPlan(plan)}
                  >
                    Edit plan
                  </Button>
                )}
              </Stack>
              <TableContainer>
                <Table
                  size="small"
                  aria-label={`Offer ranges from ${plan.effective_from}`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Item price from</TableCell>
                      <TableCell>Item price below</TableCell>
                      <TableCell align="right">Points per piece</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.tiers.map((tier, index) => (
                      <TableRow key={tier.id ?? index}>
                        <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {formatSales(tier.min_sales)}
                        </TableCell>
                        <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {tier.max_sales == null
                            ? "No upper limit"
                            : formatSales(tier.max_sales)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatPoints(tier.points)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))
        )}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Typography variant="h6">Daily points</Typography>
        <TextField
          type="date"
          size="small"
          label="Date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      {dailyLoading ? (
        <Typography>Loading daily points...</Typography>
      ) : (
        daily && (
          <>
            {!daily.plan_id && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No offer plan applied on this date.
              </Alert>
            )}
            {daily.plan_id && daily.basis === "order_items" && (
              <Typography color="text.secondary" variant="body2" mb={2}>
                {daily.orders.length
                  ? `Points come from the items on ${daily.orders.length} POS orders and are shared among ${daily.present_count} present staff.`
                  : "No POS order item earned points on this date."}
              </Typography>
            )}
            {daily.plan_id && daily.basis !== "order_items" && (
              <Typography color="text.secondary" variant="body2" mb={2}>
                {daily.tier?.exceeded
                  ? `Net sales exceeded every offer range, so the highest award applies. Points are shared among ${daily.present_count} present staff.`
                  : daily.tier
                    ? `Qualifying range: ${formatSales(daily.tier.min_sales)} ${daily.tier.max_sales == null ? "and above" : `to below ${formatSales(daily.tier.max_sales)}`}. Points are shared among ${daily.present_count} present staff.`
                    : "Net sales did not qualify for an offer range on this date."}
              </Typography>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={2}>
              {[
                daily.basis === "order_items"
                  ? ["POS orders", daily.orders_count]
                  : ["Net sales", formatSales(daily.net_sales)],
                ["Points to share", formatPoints(daily.available_points)],
                ["Points awarded", formatPoints(daily.awarded_points)],
                ["Present staff", daily.present_count],
              ].map(([label, value]) => (
                <Card
                  key={label}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">
                      {label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {value}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            {Number(daily.unallocated_points) > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {formatPoints(daily.unallocated_points)} points were not awarded
                because no staff was marked present.
              </Alert>
            )}
            {daily.orders?.length > 0 && (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell align="right">Points earned</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daily.orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.code}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatPoints(order.points)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Present staff</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {daily.staff.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {formatPoints(member.points)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {daily.staff.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        No staff marked present.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h6">Monthly point totals</Typography>
          <Typography color="text.secondary" variant="body2">
            Points are calculated from current sales and attendance records.
            Payroll conversion is not set yet.
          </Typography>
        </Box>
        <TextField
          type="month"
          size="small"
          label="Month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        {monthlyLoading ? (
          <Typography sx={{ p: 2 }}>Loading monthly points...</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Staff member</TableCell>
                <TableCell align="right">Points this month</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(monthly?.staff ?? []).map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatPoints(member.points)}
                  </TableCell>
                </TableRow>
              ))}
              {!monthly?.staff?.length && (
                <TableRow>
                  <TableCell colSpan={2}>
                    No staff records for this month.
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  <strong>Total awarded</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{formatPoints(monthly?.awarded_points)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {form?.id ? "Edit offer plan" : "New offer plan"}
        </DialogTitle>
        {form && (
          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <DialogContent>
              <Stack gap={2} sx={{ pt: 1 }}>
                <TextField
                  type="date"
                  label="Effective from"
                  value={form.effective_from}
                  disabled={Boolean(form.id)}
                  inputProps={{ min: localDate() }}
                  onChange={(event) =>
                    setForm({ ...form, effective_from: event.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  helperText="New plans can start today or later. Past plans stay locked."
                />
                {form.tiers.map((tier, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "repeat(3, minmax(0, 1fr)) 40px",
                      },
                      gap: 1.5,
                      alignItems: "start",
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label="Item price from (Rs.)"
                      helperText="This amount is included."
                      value={tier.min_sales}
                      inputProps={{ min: 0, step: "0.01" }}
                      onChange={(event) =>
                        changeTier(index, "min_sales", event.target.value)
                      }
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Item price below (Rs.)"
                      helperText="Optional. This amount is excluded."
                      value={tier.max_sales}
                      inputProps={{ min: 0, step: "0.01" }}
                      onChange={(event) =>
                        changeTier(index, "max_sales", event.target.value)
                      }
                    />
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label="Points per piece"
                      helperText="Earned by each piece sold in this range."
                      value={tier.points}
                      inputProps={{ min: 1, step: 1 }}
                      onChange={(event) =>
                        changeTier(index, "points", event.target.value)
                      }
                    />
                    <IconButton
                      aria-label={`Remove range ${index + 1}`}
                      sx={{ mt: { sm: 1 }, justifySelf: "end" }}
                      disabled={form.tiers.length === 1}
                      onClick={() =>
                        setForm({
                          ...form,
                          tiers: form.tiers.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  onClick={() =>
                    setForm({
                      ...form,
                      tiers: [
                        ...form.tiers,
                        { min_sales: "", max_sales: "", points: "" },
                      ],
                    })
                  }
                >
                  Add range
                </Button>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setForm(null)}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={save.isPending || !form.effective_from}
              >
                Save offer plan
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </Box>
  )
}
